import { api } from '@/lib/http-client';
import type { QueryParams } from '@/types/api';
import type {
  AttachGuideRequest,
  AttachGuideResult,
  CreateOrderRequest,
  GuideLabelReading,
  Order,
  OrderFilters,
  OrderWithHistory,
  ReadGuideLabelRequest,
  UpdateOrderStatusRequest,
} from '@/types/orders';

/**
 * Módulo 7 — Órdenes (`docs/ORDENES.md` §6).
 *
 * Rutas reales (`backend/src/modules/orders/orders.routes.ts`), todas bajo `authenticate` +
 * `requireTenant()`: listar y ver las puede hacer cualquier miembro del negocio (el AGENTE_HUMANO
 * atiende pedidos); crear, cambiar de estado y cargar la guía son solo ADMIN (`requireRole`).
 * El aislamiento entre negocios lo garantiza el backend: un id de otra empresa responde 404.
 *
 * Tres cosas del contrato que no se pueden adivinar:
 *  - el filtro de tamaño es `limit` (en inglés), default 50 y máximo 200, y el listado NO se
 *    pagina: devuelve las últimas `limit` órdenes ordenadas por `createdAt` descendente;
 *  - el `POST` (orden manual) responde 201 con la orden SIN `statusHistory` (el detalle sí lo trae);
 *  - cargar la guía (`PATCH .../guia`) pasa la orden a `ENVIADA` y avisa al cliente en la misma
 *    llamada, así que su respuesta no es solo la orden: ver `AttachGuideResult`.
 */

/** Filtros de pantalla → query params, sin mandar los que no van. */
export function orderQueryFrom(filters: OrderFilters = {}): QueryParams {
  const query: QueryParams = {};

  if (filters.estado) query.estado = filters.estado;
  if (filters.conversationId) query.conversationId = filters.conversationId;
  if (filters.limit !== undefined) query.limit = filters.limit;

  return query;
}

/** `GET /api/tenants/:tenantId/orders` — últimas órdenes (sin historial: eso vive en el detalle). */
export function listOrders(tenantId: string, filters: OrderFilters = {}): Promise<Order[]> {
  return api.get<Order[]>(`/api/tenants/${tenantId}/orders`, { query: orderQueryFrom(filters) });
}

/** `GET /api/tenants/:tenantId/orders/:orderId` — la orden + `statusHistory` (404 si es ajena). */
export function fetchOrder(tenantId: string, orderId: string): Promise<OrderWithHistory> {
  return api.get<OrderWithHistory>(`/api/tenants/${tenantId}/orders/${orderId}`);
}

/**
 * `POST /api/tenants/:tenantId/orders/:orderId/guia/numero` (solo ADMIN) — lee la ETIQUETA de la guía.
 *
 * Devuelve el número de guía, la transportadora y el número de pedido de la transportadora usando la
 * misma key de Gemini del negocio (Integraciones → Gemini) que la visión y los audios. **No es un
 * endpoint de errores**: el desenlace viaja en `status` (ver `GuideLabelReading`), así que la pantalla
 * precarga lo que se pudo leer y deja seguir a mano cuando no.
 */
export function readGuideLabel(
  tenantId: string,
  orderId: string,
  payload: ReadGuideLabelRequest,
): Promise<GuideLabelReading> {
  return api.post<GuideLabelReading>(
    `/api/tenants/${tenantId}/orders/${orderId}/guia/numero`,
    payload,
  );
}

/**
 * `POST /api/tenants/:tenantId/orders` (solo ADMIN) — orden cargada a mano.
 * Nace `PENDIENTE_CONFIRMACION` (la que arma el bot nace `CONFIRMADA`) y responde 201.
 */
export function createOrder(tenantId: string, payload: CreateOrderRequest): Promise<Order> {
  return api.post<Order>(`/api/tenants/${tenantId}/orders`, payload);
}

/** `PATCH .../orders/:orderId/estado` (solo ADMIN) — transición validada + fila de historial. */
export function updateOrderStatus(
  tenantId: string,
  orderId: string,
  payload: UpdateOrderStatusRequest,
): Promise<OrderWithHistory> {
  return api.patch<OrderWithHistory>(`/api/tenants/${tenantId}/orders/${orderId}/estado`, payload);
}

/**
 * `PATCH .../orders/:orderId/guia` (solo ADMIN) — sube la foto (base64) o guarda una URL externa,
 * guarda el número de guía, pasa la orden a `ENVIADA` y le manda la foto al cliente.
 *
 * La subida va en JSON (~7 MB en base64 para una foto de 5 MB) y el backend le tiene su propio
 * parser de 8 MB, montado aparte del parser global de 1 MB: no hay que usar `formData` acá.
 */
export function attachShippingGuide(
  tenantId: string,
  orderId: string,
  payload: AttachGuideRequest,
): Promise<AttachGuideResult> {
  return api.patch<AttachGuideResult>(`/api/tenants/${tenantId}/orders/${orderId}/guia`, payload);
}
